import {
  computed,
  inject,
  Injectable,
  signal,
  WritableSignal,
} from '@angular/core';
import { SelectClientById } from '@shared/abstraction/utils/select-client-by-id';
import { Client } from '@shared/models';
import { map, shareReplay, Subject } from 'rxjs';
import {
  ClientResponseAuditableODataWithMetadataResponse,
  GstarClient,
} from 'src/app/web-api-client';

@Injectable({
  providedIn: 'root',
})
export class HeaderService {
  private readonly gstarClient = inject(GstarClient);

  clientList = signal<Client[]>([]);
  selectedClient = signal<Client | undefined>(undefined);
  isClientManuallySelected: WritableSignal<boolean> = signal<boolean>(false);
  updatedClient: WritableSignal<boolean> = signal<boolean>(false);
  isDisabled = signal<boolean>(false);

  hasClient = computed(() => {
    return this.selectedClient() !== undefined;
  });

  selectedClient$ = new Subject<Client | undefined>();
  clientList$: Subject<Client[]> = new Subject();
  clearSelection$: Subject<void> = new Subject();

  setSelectedClient(client?: Client, isEmitPrevention?: boolean): void {
    this.selectedClient.set(client);
    if (!isEmitPrevention) {
      this.selectedClient$.next(client);
    }
  }

  setClientById({
    clientId,
    isEmitPrevention,
    isManuallySelected,
  }: SelectClientById): void {
    const selectedClient = this.clientList().find(
      client => client.id === clientId
    );
    if (this.selectedClient()?.id !== selectedClient?.id) {
      this.selectedClient.set(selectedClient);
    }
    this.isClientManuallySelected.set(!!isManuallySelected);
    this.setSelectedClient(selectedClient, isEmitPrevention);
  }

  reloadClientList(): void {
    this.gstarClient
      .clientsGET(
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined
      )
      .pipe(
        map(
          (clientResponse: ClientResponseAuditableODataWithMetadataResponse) =>
            clientResponse.value?.map(client => new Client(client)) ?? []
        ),
        shareReplay({ bufferSize: 1, refCount: true })
      )
      .subscribe((clients: Client[]) => {
        this.clientList.set(clients);
        this.clientList$.next(clients);
      });
  }

  disable(): void {
    this.isDisabled.set(true);
  }

  enable(): void {
    this.isDisabled.set(false);
  }

  clear(): void {
    this.clientList.set([]);
    this.clearSelection();
  }

  clearSelection(): void {
    this.updatedClient.set(true);
    this.selectedClient.set(undefined);
    this.selectedClient$.next(undefined);
    this.clearSelection$.next();
  }
}
